import * as React from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { SvgIcon, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";

import CheckboxGroup from "@/components/CheckboxGroup";
import { AIRLINE_IMG } from "@/constants/airline";
import useThemeData from "@/hooks/useThemeData";
import { renderProgramsLogo } from "@/utils/commonRender";

interface Option {
  label: React.ReactNode;
  value: string;
}

interface SearchSelectProps {
  options: Option[];
  Icon?: typeof SvgIcon;
  value: string[];
  onChange: (val: string[]) => void;
}

export default function SearchCheckboxPrograms(props: SearchSelectProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const { isDarkMode } = useThemeData();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const renderValue = React.useCallback(() => {
    const Icon = props.Icon;

    return (
      <div className="flex item-center mt-[2.5px]">
        {Icon ? (
          <Icon
            fontSize="small"
            sx={{ color: (theme) => theme.palette.text.secondary, marginRight: "8px" }}
          />
        ) : null}

        <Typography
          className="overflow-hidden text-ellipsis whitespace-nowrap max-w-[100px]"
          sx={{ lineHeight: "18px" }}
          color="textSecondary"
          variant="body2"
        >
          {props?.value?.length > 0 ? props.value.join(",") : "All Programs"}
        </Typography>
      </div>
    );
  }, [props.Icon, props.value]);

  return (
    <div>
      <Button aria-describedby={id} onClick={handleClick} className="flex items-center">
        {renderValue()}
        <span className="flex items-center">
          {open ? <ArrowDropUpIcon sx={{ color: isDarkMode ? 'white' : 'gray' }} /> : <ArrowDropDownIcon sx={{ color: isDarkMode ? 'white' : 'gray' }} />}
        </span>
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <div className="px-[20px] pt-[20px]">
          <div className={`max-h-[400px] overflow-auto`}>
            <CheckboxGroup
              options={props.options}
              value={
                props.value?.length > 0 ? props.value : props.options.map((item) => item.value)
              }
              onChange={props.onChange}
              renderPrefix={(params) => {
                return renderProgramsLogo(params.value, isDarkMode);
              }}
            />
          </div>

          <div className="text-right py-[3px]">
            <Button
              variant="text"
              onClick={() => props.onChange([])}
              sx={{
                textTransform: "capitalize",
              }}
            >
              select all
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
}
